/**
 * ComposerContextRow — the Bower entities Bower is allowed to read this turn.
 *
 * A single-line row of context CHIPS at the top of the composer, rendered only
 * when something is selected.
 *
 * The `@` ADD button is not in this row — it is `ComposerAddContextButton`,
 * which the composer places in the action row (2026-09 design: "Add context"
 * and "Sources" as pills on one row).
 *
 * That split is what keeps an old bug fixed. The add control used to live in
 * the action row ONLY while empty and jump to the top once you picked a
 * context — a control that moves the moment you use it is one you have to
 * re-find. The fix at the time was to pin both together at the top, always
 * present, even when that meant an empty row. Now the button has a fixed home
 * of its own, so it never moves AND the empty row is gone. Do not merge them
 * back together.
 *
 * Deliberately distinct from the attachment tray (ComposerAttachmentTray):
 * these are entities that already exist in Bower and persist across turns,
 * not files being staged for this one. Same rail and same chip grammar though
 * — see ComposerScrollRow / ComposerChip.
 */

import { Box, HStack, Text } from '@chakra-ui/react';

import type { ChatContext } from '../../../contexts/ChatContextProvider';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { ACTION_ROW_GLYPH_SIZE } from '../../ui/iconButtonMetrics';
import { Tooltip } from '../../ui/Tooltip';
import { COMPOSER_LABEL_CLASS } from './composerMetrics';
import { ComposerScrollRow } from './ComposerScrollRow';
import ContextChip from './ContextChip';

interface ComposerContextRowProps {
  readonly contexts: readonly ChatContext[];
  readonly onRemove: (context: ChatContext) => void;
  /** Stop sending a context's selected cells; only a context with a live
   *  selection (the spreadsheet page's) ever offers the control. */
  readonly onDismissSelection?: (context: ChatContext) => void;
}

/**
 * The pinned `@` control. Lives in the composer's action row beside Sources.
 *
 * `addContextLabel` is not decoration: the dashboard renders this composer AND
 * the panel's at the same time, so without a distinct accessible name there
 * are two identically-named buttons on screen. Same reason `sendLabel` exists.
 */
export function ComposerAddContextButton({
  onAdd,
  addContextLabel = 'Add context',
}: {
  readonly onAdd: () => void;
  readonly addContextLabel?: string;
}) {
  return (
    <Tooltip label={addContextLabel} placement="top">
      <HStack
        as="button"
        type="button"
        aria-label={addContextLabel}
        onClick={onAdd}
        spacing={1}
        flexShrink={0}
        // A pill, per the 2026-09 composer: white on the parchment box, with a
        // hairline. `bg.elevated` rather than `bg.surface` — since the card
        // inversion the latter IS the parchment the pill sits on.
        h="28px"
        px={2.5}
        borderRadius="full"
        bg="bg.elevated"
        borderWidth="1px"
        borderColor="border.default"
        color="text.secondary"
        _hover={{ bg: 'bg.hover', color: 'text.primary' }}
        _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
        transition="all 0.15s"
      >
        <MaterialIcon name="alternate_email" boxSize={ACTION_ROW_GLYPH_SIZE} />
        {/* The VISIBLE text is always the same; `addContextLabel` only
            distinguishes the two composers for assistive tech.

            `composer-label` collapses it to a bare `@` below the container
            query in ChatComposer — the same rule Sources already used, which
            this pill simply never opted into. In the 352px chat panel the
            labelled row overflowed and wrapped onto two lines; collapsing is
            what keeps the wrap as a last resort for genuinely tiny widths
            rather than the normal state of the panel. The button keeps its
            `aria-label` and its tooltip, so the name survives the collapse. */}
        <Text className={COMPOSER_LABEL_CLASS} fontSize="xs" fontWeight="medium" color="inherit">
          Add context
        </Text>
      </HStack>
    </Tooltip>
  );
}

export function ComposerContextRow({
  contexts,
  onRemove,
  onDismissSelection,
}: ComposerContextRowProps) {
  // No chips, no row. The add button has its own fixed home now, so an empty
  // row here would be empty chrome rather than a control anchor.
  if (contexts.length === 0) return null;

  return (
    <ComposerScrollRow
      ariaLabel="Chat context"
      overflowNoun={['context item', 'context items']}
      measureKey={contexts}
    >
      {contexts.map((context) => (
        <Box key={`${context.type}:${context.id}`} data-composer-chip flexShrink={0}>
          <ContextChip
            context={context}
            onRemove={() => onRemove(context)}
            onDismissSelection={onDismissSelection && (() => onDismissSelection(context))}
          />
        </Box>
      ))}
    </ComposerScrollRow>
  );
}
